import React, { createContext, useContext, useId, useState } from "react";
import { createPortal } from "react-dom";
import {
  Dialog as MuiDialog,
  Box,
  Stack,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const DialogFooterContext = createContext(null);
const toSxArray = (sx) => (Array.isArray(sx) ? sx : [sx]);
// Explicit button overrides must win over the dialog's descendant styles.
const buttonOverrides = (sx) =>
  toSxArray(sx).map((value) => (theme) => ({
    "&&": typeof value === "function" ? value(theme) : value,
  }));
const fieldsetSx = { border: 0, p: 0, m: 0, minWidth: 0 };

// Lets a form keep its action handlers locally while rendering buttons in the footer.
export function DialogFooter({ children }) {
  const container = useContext(DialogFooterContext);
  return container ? createPortal(children, container) : null;
}

const GenericDialog = ({
  open,
  onClose,
  title,
  description,
  subtitle,
  icon,
  content,
  children,
  confirmText = "Save",
  cancelText = "Cancel",
  onConfirm,
  loading = false,
  disabled = false,
  maxWidth = "sm",
  fullScreen = false,
  hideCloseButton = false,
  hideConfirmButton = false,
  actions,
  paperSx,
  headerSx,
  contentSx,
  actionsSx,
  confirmButtonSx,
  cancelButtonSx,
}) => {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const [footerContainer, setFooterContainer] = useState(null);
  const body = children !== undefined ? children : content;
  const controlsDisabled = loading || disabled;

  return (
    <DialogFooterContext.Provider value={footerContainer}>
      <MuiDialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth={maxWidth}
        fullWidth
        fullScreen={fullScreen}
        scroll='paper'
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={title && description ? descriptionId : undefined}
        PaperProps={{
          sx: [
            {
              borderRadius: fullScreen ? 0 : 2.5,
              border: fullScreen ? 0 : 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              backgroundImage: "none",
              m: fullScreen ? 0 : { xs: 2, sm: 4 },
              width: fullScreen ? "100%" : { xs: "calc(100% - 32px)", sm: "100%" },
              maxHeight: fullScreen ? "100%" : "calc(100% - 32px)",
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              "& .MuiButton-root": {
                textTransform: "none",
                borderRadius: 1.5,
                fontWeight: 600,
              },
              "& .MuiButton-contained": {
                minHeight: 40,
                px: 2.5,
                boxShadow: "none",
              },
            },
            ...toSxArray(paperSx),
          ],
        }}
      >
        {title ? (
          <Box
            sx={[
              {
                p: { xs: 2, sm: 2.5 },
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
              },
              ...toSxArray(headerSx),
            ]}
          >
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='flex-start'
              gap={2}
            >
              {icon && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                >
                  {icon}
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                <Typography id={titleId} component='h2' variant='subtitle1' fontWeight={600}>
                  {title}
                </Typography>
                {description && (
                  <Typography
                    id={descriptionId}
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5, lineHeight: 1.6 }}
                  >
                    {description}
                  </Typography>
                )}
                {subtitle && (
                  <Typography variant='caption' color='text.secondary' sx={{ display: "block" }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {!hideCloseButton && (
                <IconButton
                  aria-label='Close dialog'
                  onClick={onClose}
                  disabled={loading}
                  size='small'
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          </Box>
        ) : null}

        <DialogContent
          sx={[
            {
              p: { xs: 2, sm: 2.5 },
            },
            ...toSxArray(contentSx),
          ]}
        >
          <Box component='fieldset' disabled={controlsDisabled} sx={fieldsetSx}>
            {typeof body === "string" ? <Typography>{body}</Typography> : body}
          </Box>
        </DialogContent>

        <DialogActions
          sx={[
            {
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
              flexWrap: "wrap",
              gap: 1,
              "& > :not(style) ~ :not(style)": { ml: 0 },
              "& .MuiButton-root": { minHeight: 40 },
            },
            ...toSxArray(actionsSx),
          ]}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            variant='text'
            color='inherit'
            sx={buttonOverrides(cancelButtonSx)}
          >
            {cancelText}
          </Button>
          <Box
            component='fieldset'
            disabled={controlsDisabled}
            sx={{
              ...fieldsetSx,
              maxWidth: "100%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: 1,
              "&:empty": { display: "none" },
            }}
            ref={setFooterContainer}
          >
            {actions !== undefined ? actions : (
              !hideConfirmButton && (
                <Button
                  variant='contained'
                  onClick={onConfirm}
                  disabled={controlsDisabled}
                  sx={buttonOverrides(confirmButtonSx)}
                >
                  {loading ? "Processing..." : confirmText}
                </Button>
              )
            )}
          </Box>
        </DialogActions>
      </MuiDialog>
    </DialogFooterContext.Provider>
  );
};

export default GenericDialog;
